/**
 * InvestmentBaselinesPanel — admin CRUD para overrides de rentabilidad
 * y valorización por ciudad + barrio + estrato + tipo.
 *
 * Los valores heurísticos base son inmutables; sólo los overrides
 * mostrados aquí se editan desde este panel.
 */
import React, { useEffect, useState } from 'react';
import { TrendingUp, Plus, Trash2, RefreshCw, Info } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

export default function InvestmentBaselinesPanel() {
  const { toast } = useToast();
  const [overrides, setOverrides] = useState([]);
  const [heuristic, setHeuristic] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    city: '',
    neighborhood: '',
    stratum: '',
    property_type: '',
    rent_yield_pct: '',
    appreciation_pct: '',
    note: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [list, table] = await Promise.all([
        api.listInvestmentBaselines(),
        api.investmentHeuristicTable(),
      ]);
      setOverrides(list.items || []);
      setHeuristic(table.cities || []);
    } catch (err) {
      toast({ title: 'Error cargando baselines', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.city.trim()) {
      toast({ title: 'Falta la ciudad', description: 'La ciudad es obligatoria.', variant: 'destructive' });
      return;
    }
    const y = parseFloat(form.rent_yield_pct);
    const a = parseFloat(form.appreciation_pct);
    if (Number.isNaN(y) || Number.isNaN(a)) {
      toast({ title: 'Números inválidos', description: 'Rent yield y appreciation deben ser numéricos.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.upsertInvestmentBaseline({
        city: form.city.trim(),
        neighborhood: form.neighborhood.trim() || null,
        stratum: form.stratum ? parseInt(form.stratum, 10) : null,
        property_type: form.property_type.trim() || null,
        rent_yield_pct: y,
        appreciation_pct: a,
        note: form.note.trim() || null,
      });
      toast({ title: 'Baseline guardado', description: 'Los cambios ya afectan a las recomendaciones del Advisor.' });
      setForm({ city: '', neighborhood: '', stratum: '', property_type: '', rent_yield_pct: '', appreciation_pct: '', note: '' });
      await load();
    } catch (err) {
      toast({ title: 'No pudimos guardar', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!confirm(`¿Eliminar baseline para ${b.city}${b.neighborhood ? ' · ' + b.neighborhood : ''}${b.stratum ? ' · e' + b.stratum : ''}${b.property_type ? ' · ' + b.property_type : ''}?`)) return;
    try {
      await api.deleteInvestmentBaseline({
        city: b.city,
        neighborhood: b.neighborhood,
        stratum: b.stratum,
        property_type: b.property_type,
      });
      toast({ title: 'Baseline eliminado' });
      await load();
    } catch (err) {
      toast({ title: 'No pudimos eliminar', description: err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="investment-baselines-panel">
      {/* Intro */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-habita-mint text-habita grid place-items-center shrink-0">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif text-xl font-bold text-habita-dark">Baselines de Inversión</h2>
            <p className="text-sm text-gray-600 mt-1">
              El Advisor calcula ROI y valorización usando esta tabla. Sin override, aplica la heurística por ciudad + estrato.
              Cualquier override que agregues aquí <strong>reemplaza</strong> el valor heurístico para ese segmento.
            </p>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-habita-dark mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Crear / actualizar override
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Ciudad *</Label>
            <Input value={form.city} onChange={(e) => setField('city', e.target.value)} placeholder="Ej: Medellín" data-testid="baseline-city" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Barrio (opcional)</Label>
            <Input value={form.neighborhood} onChange={(e) => setField('neighborhood', e.target.value)} placeholder="Ej: El Poblado" data-testid="baseline-neighborhood" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Estrato (1-6, opcional)</Label>
            <Input type="number" min="1" max="6" value={form.stratum} onChange={(e) => setField('stratum', e.target.value)} placeholder="Ej: 5" data-testid="baseline-stratum" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Tipo de inmueble (opcional)</Label>
            <Input value={form.property_type} onChange={(e) => setField('property_type', e.target.value)} placeholder="Ej: apartamento" data-testid="baseline-type" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Renta anual (%) *</Label>
            <Input type="number" step="0.1" value={form.rent_yield_pct} onChange={(e) => setField('rent_yield_pct', e.target.value)} placeholder="Ej: 6.5" data-testid="baseline-yield" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Valorización anual (%) *</Label>
            <Input type="number" step="0.1" value={form.appreciation_pct} onChange={(e) => setField('appreciation_pct', e.target.value)} placeholder="Ej: 5.0" data-testid="baseline-appreciation" className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-gray-500">Nota interna (opcional)</Label>
            <Input value={form.note} onChange={(e) => setField('note', e.target.value)} placeholder="Ej: Ajustado tras estudio Camacol Q1 2026" className="mt-1.5" data-testid="baseline-note" />
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <Button onClick={submit} disabled={saving} className="btn-habita rounded-full h-11 px-6" data-testid="baseline-save-btn">
            {saving ? 'Guardando...' : 'Guardar baseline'}
          </Button>
          <Button variant="outline" onClick={load} className="rounded-full h-11 px-5">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Recargar
          </Button>
        </div>
      </div>

      {/* Lista de overrides */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-habita-dark mb-4">Overrides activos ({overrides.length})</h3>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Cargando...</p>
        ) : overrides.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">Aún no hay overrides. El Advisor usa la heurística por defecto.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="baselines-table">
              <thead className="bg-habita-cream text-left">
                <tr>
                  <th className="p-2.5 font-semibold text-habita-dark">Ciudad</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Barrio</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Estrato</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Tipo</th>
                  <th className="p-2.5 font-semibold text-habita-dark text-right">Renta %</th>
                  <th className="p-2.5 font-semibold text-habita-dark text-right">Valorización %</th>
                  <th className="p-2.5 font-semibold text-habita-dark">Nota</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {overrides.map((b, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="p-2.5 font-medium text-habita-dark">{b.city}</td>
                    <td className="p-2.5 text-gray-700">{b.neighborhood || '—'}</td>
                    <td className="p-2.5 text-gray-700">{b.stratum != null ? `e${b.stratum}` : '—'}</td>
                    <td className="p-2.5 text-gray-700">{b.property_type || '—'}</td>
                    <td className="p-2.5 text-right font-semibold text-habita">{b.rent_yield_pct}%</td>
                    <td className="p-2.5 text-right font-semibold text-habita">{b.appreciation_pct}%</td>
                    <td className="p-2.5 text-gray-500 text-xs italic">{b.note || ''}</td>
                    <td className="p-2.5 text-right">
                      <button onClick={() => remove(b)} className="text-rose-600 hover:bg-rose-50 rounded-full p-1.5" data-testid={`baseline-delete-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Heurística base (referencia read-only) */}
      <div className="bg-habita-cream/60 rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-habita-dark mb-3 flex items-center gap-2">
          <Info className="w-4 h-4" /> Heurística base (referencia)
        </h3>
        <p className="text-xs text-gray-600 mb-4">
          Estos valores se aplican automáticamente cuando no hay un override específico. Son conservadores y basados en referencias públicas (Camacol, Fedelonjas 2024-25).
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {heuristic.map((c) => (
            <div key={c.city} className="bg-white rounded-xl p-3 border border-gray-100">
              <p className="font-semibold text-habita-dark">{c.city}</p>
              <div className="mt-2 space-y-1 text-xs">
                {c.strata.map((s) => (
                  <div key={s.stratum} className="flex justify-between text-gray-600">
                    <span>Estrato {s.stratum}</span>
                    <span>
                      <span className="font-semibold text-habita">{s.rent_yield_pct}%</span>
                      <span className="mx-1 text-gray-400">·</span>
                      <span className="font-semibold text-habita">{s.appreciation_pct}%</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
